import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { apiClient } from '../utils/api';
import { Draft, Player, Team, PlayerSearchParams } from '../types';
import { 
  Container, 
  Card, 
  Button, 
  Input, 
  LoadingSpinner,
  colors,
  spacing,
  typography,
  media,
  shadows,
  borderRadius
} from '../styles/GlobalStyles';
import { 
  Search, 
  Filter, 
  Users, 
  Trophy,
  Activity,
  Menu,
  X,
  ChevronDown,
  RefreshCw,
  Plus,
  ArrowUp,
  ArrowDown,
  Sparkles,
  GripVertical
} from 'lucide-react';
import toast from 'react-hot-toast';
import AuctionBidding from '../components/AuctionBidding';
import Chat from '../components/Chat';
import { usePlayerUpdates } from '../hooks/usePlayerUpdates';

const DraftContainer = styled.div`
  min-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  background: #000000;
`;

const DraftHeader = styled.div`
  background: rgba(10, 10, 10, 0.95);
  border-bottom: 1px solid ${colors.grey[800]};
  padding: ${spacing[3]} ${spacing[4]};
  position: sticky;
  top: 80px;
  z-index: 10;
  backdrop-filter: blur(6px);
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  background: linear-gradient(180deg, ${colors.grey[950]}, #050505);
  border: 1px solid ${colors.grey[800]};
  border-radius: ${borderRadius.lg};
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.28);

  ${media.tablet} {
    padding: ${spacing[4]} ${spacing[5]};
  }
`;

const DraftTopRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;

  ${media.tablet} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const DraftTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  min-width: 0;
`;

const DraftTitle = styled.h1`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DraftStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};
  flex-wrap: wrap;
`;

const StatusBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'status'
})<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${borderRadius.base};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  text-transform: uppercase;
  
  ${props => {
    switch (props.status) {
      case 'active':
        return `
          background: ${colors.green};
          color: ${colors.black};
          font-weight: ${typography.fontWeight.bold};
        `;
      case 'paused':
        return `
          background: ${colors.warning}20;
          color: ${colors.warning};
        `;
      case 'completed':
        return `
          background: ${colors.info}20;
          color: ${colors.info};
        `;
      default:
        return `
          background: ${colors.grey[700]};
          color: ${colors.grey[300]};
        `;
    }
  }}
`;

const TurnBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'isUserTurn'
})<{ isUserTurn: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${borderRadius.base};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => (props.isUserTurn ? colors.black : colors.grey[200])};
  background: ${props => (props.isUserTurn ? colors.green : colors.grey[800])};
  border: 1px solid ${props => (props.isUserTurn ? colors.green : colors.grey[700])};
`;

const DraftSummaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[3]};
  width: 100%;

  ${media.tablet} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const SummaryCard = styled.div`
  border: 1px solid ${colors.grey[700]};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[3]} ${spacing[4]};
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.01));
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.2);
`;

const SummaryLabel = styled.div`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.xs};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: ${spacing[1]};
`;

const SummaryValue = styled.div`
  color: ${colors.white};
  font-size: clamp(1.25rem, 1.5vw, 1.75rem);
  font-weight: ${typography.fontWeight.bold};
`;

const SummaryHint = styled.div`
  margin-top: ${spacing[1]};
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.xs};
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: ${colors.grey[800]};
  overflow: hidden;
  margin-top: ${spacing[2]};
`;

const ProgressFill = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'percent'
})<{ percent: number }>`
  height: 100%;
  width: ${props => props.percent}%;
  background: linear-gradient(90deg, ${colors.green}, #7dff9e);
`;



const DraftBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  padding: ${spacing[4]};
  gap: ${spacing[4]};

  ${media.tablet} {
    flex-direction: row;
    padding: ${spacing[6]};
    gap: ${spacing[6]};
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
`;

const SearchSection = styled(Card)`
  padding: ${spacing[4]};
  border: 1px solid ${colors.grey[800]};
  border-radius: ${borderRadius.lg};
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
`;

const SearchBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[4]};

  ${media.tablet} {
    flex-direction: row;
  }
`;

const SearchInput = styled(Input)`
  width: 100%;
  padding-right: ${spacing[10]};
`;

const SearchInputWrapper = styled.div`
  flex: 1;
  position: relative;
  min-width: 0;
`;

const ClearSearchButton = styled.button`
  position: absolute;
  top: 50%;
  right: ${spacing[3]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: ${borderRadius.sm};
  background: transparent;
  color: ${colors.grey[400]};
  cursor: pointer;
  transform: translateY(-50%);

  &:hover {
    background: ${colors.grey[800]};
    color: ${colors.white};
  }

  &:focus-visible {
    outline: 2px solid ${colors.green};
    outline-offset: 2px;
  }
`;

const FilterButton = styled(Button)`
  min-width: auto;
  padding: ${spacing[3]};
`;

const SortSelect = styled.select`
  min-height: 44px;
  border: 1px solid ${colors.grey[700]};
  background: ${colors.grey[900]};
  color: ${colors.white};
  border-radius: ${borderRadius.md};
  padding: 0 ${spacing[3]};
  font-size: ${typography.fontSize.sm};

  &:focus {
    outline: none;
    border-color: ${colors.green};
  }
`;

const PositionFilters = styled.div`
  display: flex;
  gap: ${spacing[2]};
  flex-wrap: wrap;
`;

const PositionFilter = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'active'
})<{ active: boolean }>`
  padding: ${spacing[3]} ${spacing[4]};
  border: 1px solid ${colors.grey[600]};
  border-radius: ${borderRadius.md};
  background: ${props => props.active ? colors.green : 'transparent'};
  color: ${props => props.active ? colors.black : colors.grey[300]};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 44px; /* Minimum touch target size for mobile */
  min-width: 60px;

  &:hover {
    background: ${props => props.active ? colors.green : colors.grey[800]};
    color: ${props => props.active ? colors.black : colors.white};
  }
`;

const PlayersGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[3]};
  max-height: 74vh;
  overflow-y: auto;

  ${media.tablet} {
    grid-template-columns: repeat(2, 1fr);
    max-height: 92vh;
  }

  ${media.desktop} {
    grid-template-columns: repeat(3, 1fr);
    max-height: 118vh;
  }
`;

const PlayerCard = styled(Card).withConfig({
  shouldForwardProp: (prop) => prop !== 'isDrafted'
})<{ isDrafted?: boolean }>`
  padding: ${spacing[4]};
  border: 1px solid ${colors.grey[800]};
  border-radius: ${borderRadius.lg};
  cursor: ${props => props.isDrafted ? 'default' : 'pointer'};
  opacity: ${props => props.isDrafted ? 0.6 : 1};
  transition: all 0.2s ease;
  min-height: 120px; /* Ensure adequate touch target */
  
  &:hover {
    transform: ${props => props.isDrafted ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.isDrafted ? shadows.base : shadows.lg};
  }

  &:active {
    transform: ${props => props.isDrafted ? 'none' : 'scale(0.98)'};
  }
`;

const PlayerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing[3]};
`;

const PlayerIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  min-width: 0;
`;

const PlayerAvatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid ${colors.grey[700]};
  background: ${colors.grey[900]};
  flex-shrink: 0;
`;

const PlayerAvatarFallback = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  background: linear-gradient(145deg, ${colors.grey[700]}, ${colors.grey[900]});
  border: 1px solid ${colors.grey[700]};
  flex-shrink: 0;
`;

const PlayerInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PlayerName = styled.h3`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  margin: 0 0 ${spacing[1]} 0;
  line-height: 1.2;
`;

const PlayerDetails = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlayerBadges = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  align-items: flex-end;
  flex-shrink: 0;
`;

const PlayerQuickActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};

  @media (max-width: 767px) {
    display: none;
  }
`;

const PlayerActionButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== '$active'
})<{ $active?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: ${borderRadius.base};
  border: 1px solid ${props => props.$active ? colors.green : colors.grey[700]};
  background: ${props => props.$active ? `${colors.green}20` : 'transparent'};
  color: ${props => props.$active ? colors.green : colors.grey[300]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.green};
    color: ${colors.green};
  }
`;

const PositionBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'position'
})<{ position: string }>`
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${borderRadius.base};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  background: ${props => colors.position[props.position as keyof typeof colors.position] || colors.grey[600]};
`;

const InjuryBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== '$healthy'
})<{ $healthy: boolean }>`
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.base};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.$healthy ? colors.success : colors.warning};
  border: 1px solid ${props => props.$healthy ? `${colors.success}55` : `${colors.warning}55`};
  background: ${props => props.$healthy ? `${colors.success}15` : `${colors.warning}15`};
`;

const PlayerStats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${spacing[2]};
  margin-top: ${spacing[3]};
  padding-top: ${spacing[3]};
  border-top: 1px solid ${colors.grey[700]};
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing[1]};
`;

const StatValue = styled.span`
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
`;

const StatLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.grey[400]};
`;

const Sidebar = styled.div<{ isOpen?: boolean }>`
  width: 100%;
  display: none;
  flex-direction: column;
  gap: ${spacing[4]};

  ${media.tablet} {
    display: flex;
    width: 350px;
    flex-shrink: 0;
  }

  ${media.desktop} {
    width: 400px;
  }
`;

const MobileSidebarToggle = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[4]};

  ${media.tablet} {
    display: none;
  }
`;

const MobileSidebarOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 1000;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: ${spacing[4]};

  ${media.tablet} {
    display: none;
  }
`;

const MobileSidebarContent = styled.div`
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  background: ${colors.grey[900]};
  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.grey[700]};
  padding: ${spacing[4]};
`;

const MobileSidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing[4]};

  ${media.tablet} {
    display: none;
  }
`;

const TeamsPanel = styled(Card)`
  padding: ${spacing[4]};
  border: 1px solid ${colors.grey[800]};
  border-radius: ${borderRadius.lg};
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
`;

const StrategyPanel = styled(Card)`
  padding: ${spacing[4]};
  border: 1px solid ${colors.grey[800]};
  border-radius: ${borderRadius.lg};
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
`;

const StrategySection = styled.div`
  margin-top: ${spacing[3]};

  &:first-of-type {
    margin-top: 0;
  }
`;

const StrategyLabel = styled.h4`
  margin: 0 0 ${spacing[2]} 0;
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[300]};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const StrategyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const StrategyItem = styled.div`
  border: 1px solid ${colors.grey[800]};
  border-radius: ${borderRadius.md};
  background: ${colors.grey[950]};
  padding: ${spacing[2]} ${spacing[3]};
`;

const StrategyItemTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
`;

const StrategyPlayerName = styled.div`
  color: ${colors.white};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
`;

const StrategyMeta = styled.div`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.xs};
  margin-top: ${spacing[1]};
`;

const StrategyActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
`;

const IconActionButton = styled.button`
  width: 24px;
  height: 24px;
  border: 1px solid ${colors.grey[700]};
  border-radius: ${borderRadius.base};
  background: transparent;
  color: ${colors.grey[300]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    border-color: ${colors.green};
    color: ${colors.green};
  }
`;

const EmptyStrategy = styled.div`
  border: 1px dashed ${colors.grey[700]};
  border-radius: ${borderRadius.md};
  padding: ${spacing[3]};
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.sm};
  background: ${colors.grey[950]};
`;

const PanelTitle = styled.h3`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin: 0 0 ${spacing[4]} 0;
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const TeamsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const TeamItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isCurrentTurn', 'isUserTeam', 'isDragging', 'isDragOver'].includes(prop)
})<{ isCurrentTurn?: boolean; isUserTeam?: boolean; isDragging?: boolean; isDragOver?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: between;
  padding: ${spacing[4]};
  border-radius: ${borderRadius.md};
  cursor: grab;
  opacity: ${props => props.isDragging ? 0.55 : 1};
  background: ${props => {
    if (props.isCurrentTurn) return `${colors.green}20`;
    if (props.isUserTeam) return `${colors.info}20`;
    return colors.grey[950];
  }};
  border: 1px solid ${props => {
    if (props.isDragOver) return colors.green;
    if (props.isCurrentTurn) return colors.green;
    if (props.isUserTeam) return colors.info;
    return colors.grey[800];
  }};

  &:active {
    cursor: grabbing;
  }
`;

const TeamDragHandle = styled.span`
  display: inline-flex;
  color: ${colors.grey[500]};
  margin-right: ${spacing[2]};
  flex-shrink: 0;
`;

const MobileTeamItem = styled(TeamItem)`
  width: 100%;
  cursor: pointer;
  font: inherit;
  text-align: left;
`;

const MobileTeamRoster = styled.div`
  border: 1px solid ${colors.grey[800]};
  border-top: none;
  border-radius: 0 0 ${borderRadius.md} ${borderRadius.md};
  background: ${colors.grey[950]};
  padding: ${spacing[2]} ${spacing[3]} ${spacing[3]};
`;

const MobileRosterPlayer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${spacing[2]};
  padding: ${spacing[2]} 0;
  border-bottom: 1px solid ${colors.grey[800]};
  color: ${colors.grey[300]};
  font-size: ${typography.fontSize.sm};

  &:last-child {
    border-bottom: none;
  }
`;

const MobileRosterEmpty = styled.div`
  padding-top: ${spacing[1]};
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.sm};
`;

const TeamInfo = styled.div`
  flex: 1;
`;

const TeamName = styled.div`
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  font-size: ${typography.fontSize.sm};
`;

const TeamStats = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.grey[400]};
  margin-top: ${spacing[1]};
`;

const BudgetDisplay = styled.div`
  text-align: right;
  min-width: 140px;
`;

const BudgetAmount = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'remainingPercent'
})<{ remainingPercent: number }>`
  font-weight: ${typography.fontWeight.bold};
  font-size: ${typography.fontSize.sm};
  color: ${props => {
    if (props.remainingPercent < 15) return colors.error;
    if (props.remainingPercent < 30) return colors.warning;
    return colors.green;
  }};
`;

const BudgetLabel = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.grey[300]};
  font-weight: ${typography.fontWeight.medium};
`;

const BudgetMeta = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing[2]};
  font-size: ${typography.fontSize.xs};
  color: ${colors.grey[400]};
  margin-top: ${spacing[1]};
`;

const BudgetBarTrack = styled.div`
  width: 136px;
  height: 6px;
  border-radius: 999px;
  background: ${colors.grey[800]};
  overflow: hidden;
  display: flex;
  margin-top: ${spacing[2]};
  margin-left: auto;
`;

const BudgetSpentSegment = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'percent'
})<{ percent: number }>`
  width: ${props => props.percent}%;
  background: ${colors.grey[500]};
`;

const BudgetRemainingSegment = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'percent' && prop !== 'remainingPercent'
})<{ percent: number; remainingPercent: number }>`
  width: ${props => props.percent}%;
  background: ${props => {
    if (props.remainingPercent < 15) return colors.error;
    if (props.remainingPercent < 30) return colors.warning;
    return colors.green;
  }};
`;

const ActivityPanel = styled(Card)`
  padding: ${spacing[4]};
  border: 1px solid ${colors.grey[800]};
  border-radius: ${borderRadius.lg};
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
`;

const ActivityList = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  max-height: 220px;
  overflow-y: auto;
  padding-left: ${spacing[3]};

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 4px;
    bottom: 4px;
    width: 2px;
    background: linear-gradient(180deg, ${colors.grey[700]}, ${colors.grey[900]});
    border-radius: 999px;
  }
`;

const ActivityItem = styled.div`
  position: relative;
  padding-left: ${spacing[4]};
`;

const ActivityDot = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'kind'
})<{ kind: 'nomination' | 'bid' | 'win' | 'admin' | 'system' }>`
  position: absolute;
  left: -1px;
  top: 11px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid ${colors.grey[950]};
  background: ${props => {
    switch (props.kind) {
      case 'nomination':
        return colors.info;
      case 'bid':
        return colors.warning;
      case 'win':
        return colors.green;
      case 'admin':
        return '#f59e0b';
      default:
        return colors.grey[500];
    }
  }};
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.02);
`;

const ActivityContent = styled.div`
  border: 1px solid ${colors.grey[800]};
  border-radius: ${borderRadius.md};
  padding: ${spacing[2]} ${spacing[3]};
  background: ${colors.grey[950]};
`;

const ActivityTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing[2]};
`;

const ActivityTypeBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'kind'
})<{ kind: 'nomination' | 'bid' | 'win' | 'admin' | 'system' }>`
  display: inline-flex;
  align-items: center;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.base};
  color: ${props => {
    switch (props.kind) {
      case 'nomination':
        return '#8ec8ff';
      case 'bid':
        return '#ffd27a';
      case 'win':
        return '#90f5a6';
      case 'admin':
        return '#f6c06c';
      default:
        return colors.grey[400];
    }
  }};
  background: ${props => {
    switch (props.kind) {
      case 'nomination':
        return `${colors.info}20`;
      case 'bid':
        return `${colors.warning}20`;
      case 'win':
        return `${colors.success}22`;
      case 'admin':
        return '#f59e0b24';
      default:
        return `${colors.grey[500]}22`;
    }
  }};
`;

const ActivityTime = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.grey[500]};
`;

const ActivityMessage = styled.p`
  margin: ${spacing[1]} 0 0;
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[200]};
  line-height: 1.5;
`;

const ActivityEmptyState = styled.div`
  border: 1px dashed ${colors.grey[700]};
  border-radius: ${borderRadius.md};
  padding: ${spacing[3]};
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.sm};
  background: ${colors.grey[950]};
`;

const AssignModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[4]};
  z-index: 1200;

  @media (max-width: 767px) {
    align-items: flex-end;
    padding: ${spacing[2]};
  }
`;

const AssignModalContent = styled(Card)`
  width: 100%;
  max-width: 520px;
  padding: ${spacing[5]};

  @media (max-width: 767px) {
    max-width: 100%;
    border-radius: ${borderRadius.lg} ${borderRadius.lg} 0 0;
    max-height: 88vh;
    overflow-y: auto;
    padding: ${spacing[4]};
  }
`;

const AssignModalTitle = styled.h3`
  margin: 0 0 ${spacing[3]} 0;
  color: ${colors.white};
  font-size: ${typography.fontSize.lg};
`;

const AssignModalText = styled.p`
  margin: 0 0 ${spacing[4]} 0;
  color: ${colors.grey[300]};
  font-size: ${typography.fontSize.sm};
`;

const FormRow = styled.div`
  margin-bottom: ${spacing[4]};
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: ${spacing[2]};
  color: ${colors.grey[300]};
  font-size: ${typography.fontSize.sm};
`;

const Select = styled.select`
  width: 100%;
  background: ${colors.grey[900]};
  border: 1px solid ${colors.grey[700]};
  color: ${colors.white};
  border-radius: ${borderRadius.md};
  padding: ${spacing[3]};
  font-size: ${typography.fontSize.sm};

  &:focus {
    outline: none;
    border-color: ${colors.green};
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing[3]};

  @media (max-width: 767px) {
    position: sticky;
    bottom: 0;
    background: ${colors.grey[950]};
    padding-top: ${spacing[3]};

    button {
      flex: 1;
    }
  }
`;

const NewsLinkButton = styled.button`
  margin-top: ${spacing[2]};
  background: transparent;
  border: none;
  padding: 0;
  color: ${colors.info};
  text-decoration: underline;
  font-size: ${typography.fontSize.sm};
  cursor: pointer;

  &:hover {
    color: ${colors.white};
  }
`;

const NewsModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[4]};
  z-index: 1300;

  @media (max-width: 767px) {
    align-items: flex-end;
    padding: ${spacing[2]};
  }
`;

const NewsModalContent = styled(Card)`
  width: 100%;
  max-width: 760px;
  max-height: 82vh;
  overflow-y: auto;
  padding: ${spacing[5]};

  @media (max-width: 767px) {
    max-width: 100%;
    max-height: 88vh;
    border-radius: ${borderRadius.lg} ${borderRadius.lg} 0 0;
    padding: ${spacing[4]};
  }
`;

const NewsModalTitle = styled.h3`
  color: ${colors.white};
  margin: 0 0 ${spacing[2]} 0;
`;

const NewsMeta = styled.p`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.sm};
  margin: 0 0 ${spacing[4]} 0;
`;

const NewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const NewsItemCard = styled.div`
  border: 1px solid ${colors.grey[700]};
  border-radius: ${borderRadius.md};
  background: ${colors.grey[950]};
  padding: ${spacing[3]};
`;

const NewsItemTitle = styled.h4`
  margin: 0 0 ${spacing[2]} 0;
  color: ${colors.white};
  font-size: ${typography.fontSize.base};
`;

const NewsItemBody = styled.p`
  margin: 0 0 ${spacing[2]} 0;
  color: ${colors.grey[300]};
  font-size: ${typography.fontSize.sm};
  line-height: ${typography.lineHeight.normal};
`;

const NewsItemMeta = styled.div`
  display: flex;
  gap: ${spacing[2]};
  flex-wrap: wrap;
  font-size: ${typography.fontSize.xs};
  color: ${colors.grey[500]};
`;

type PlayerNewsItem = {
  id: string;
  title: string;
  content: string;
  source: string;
  publishedAt?: string | null;
  updatedAt?: string | null;
  url?: string | null;
};

type ActivityEntry = {
  id: string;
  kind: 'nomination' | 'bid' | 'win' | 'admin' | 'system';
  message: string;
  timestamp: string;
};

const CostQuickActions = styled.div`
  display: flex;
  gap: ${spacing[2]};
  flex-wrap: wrap;
  margin-top: ${spacing[2]};
`;

const CostQuickButton = styled(Button)`
  min-width: 62px;
`;

const MobileQuickDock = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 35;
  display: flex;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]} calc(${spacing[2]} + env(safe-area-inset-bottom));
  background: rgba(0, 0, 0, 0.92);
  border-top: 1px solid ${colors.grey[700]};

  ${media.tablet} {
    display: none;
  }
`;

const MobileQuickDockSpacer = styled.div`
  height: 76px;

  ${media.tablet} {
    display: none;
  }
`;

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

const getMaxBidNow = (team: Team, playersPerTeam: number) => {
  const slotsRemaining = Math.max(playersPerTeam - team.players.length, 0);
  if (slotsRemaining <= 0) {
    return 0;
  }

  const reserveForOtherSlots = Math.max(slotsRemaining - 1, 0);
  return Math.max(0, team.budget.remaining - reserveForOtherSlots);
};

const formatActivityTime = (isoTime: string) => {
  const parsed = new Date(isoTime);
  if (Number.isNaN(parsed.getTime())) {
    return '--:--';
  }
  return parsed.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const getActivityLabel = (kind: ActivityEntry['kind']) => {
  if (kind === 'win') {
    return 'WIN';
  }

  return kind;
};

const normalizeName = (value?: string | null) => String(value || '').trim().toLowerCase();

function getPlayerInitials(name: string, position: string) {
  const safeName = String(name || '').trim();
  if (!safeName) {
    return position || 'NFL';
  }

  const parts = safeName.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

const DraftPage: React.FC = () => {
  const { draftId } = useParams<{ draftId: string }>();
  const { user } = useAuth();
  const { socket, joinDraft, leaveDraft, isConnected, activeUsers } = useSocket();
  
  const [draft, setDraft] = useState<Draft | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPosition, setSelectedPosition] = useState<string>('ALL');
  const [selectedSort, setSelectedSort] = useState<'best' | 'adp' | 'projection' | 'name'>('projection');
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [expandedMobileTeamId, setExpandedMobileTeamId] = useState<string | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [assignTeamId, setAssignTeamId] = useState('');
  const [assignCost, setAssignCost] = useState('1');
  const [assigning, setAssigning] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [newsLoading, setNewsLoading] = useState(false);
  const [playerNews, setPlayerNews] = useState<PlayerNewsItem[]>([]);
  const [newsError, setNewsError] = useState<string | null>(null);
  const [activityFeed, setActivityFeed] = useState<ActivityEntry[]>([]);
  const [queuePlayers, setQueuePlayers] = useState<Player[]>([]);
  const [budgetTeamOrderIds, setBudgetTeamOrderIds] = useState<string[]>([]);
  const [draggingBudgetTeamId, setDraggingBudgetTeamId] = useState<string | null>(null);
  const [budgetDragOverTeamId, setBudgetDragOverTeamId] = useState<string | null>(null);

  const positions = ['ALL', 'QB', 'RB', 'WR', 'TE', 'K', 'DEF'];

  const loadDraft = useCallback(async () => {
    if (!draftId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await apiClient.drafts.getById(draftId);
      if (response.success) {
        setDraft(response.data.draft);
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
      toast.error('Failed to load draft');
    } finally {
      setLoading(false);
    }
  }, [draftId]);

  const refreshDraft = useCallback(async () => {
    if (!draftId) {
      return;
    }

    try {
      const response = await apiClient.drafts.getById(draftId);
      if (response.success) {
        setDraft(response.data.draft);
      }
    } catch (error) {
      console.error('Failed to refresh draft:', error);
    }
  }, [draftId]);

  const searchPlayers = useCallback(async () => {
    try {
      setSearchLoading(true);
      const effectiveSort = selectedPosition === 'ALL' ? selectedSort : 'projection';
      const params: PlayerSearchParams = {
        q: searchQuery,
        sortBy: effectiveSort,
        limit: searchQuery.trim()
          ? 50
          : selectedPosition === 'ALL'
            ? 150
            : 300
      };

      if (selectedPosition !== 'ALL') {
        params.position = selectedPosition;
      }

      const response = await apiClient.players.search(params);
      if (response.success) {
        const availablePlayers = (response.data.players || []).filter(
          (player: Player) => !player.isDrafted && !player.draftedBy
        );
        setPlayers(availablePlayers);
      }
    } catch (error) {
      console.error('Failed to search players:', error);
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery, selectedPosition, selectedSort]);

  const refreshPlayers = useCallback(async () => {
    try {
      setRefreshing(true);
      await searchPlayers();
    } finally {
      setRefreshing(false);
    }
  }, [searchPlayers]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  useEffect(() => {
    if (!draft?._id) {
      return;
    }

    const firstRoundPicks = draft.draftOrder?.find((round) => round.round === 1)?.picks || [];
    const teamOrderIndex = new Map(
      firstRoundPicks.map((pick, index) => [
        typeof pick.teamId === 'string' ? pick.teamId : pick.teamId?._id,
        index
      ])
    );
    const defaultOrderIds = [...draft.teams]
      .sort((left, right) => (teamOrderIndex.get(left._id) ?? left.teamNumber) - (teamOrderIndex.get(right._id) ?? right.teamNumber))
      .map((team) => team._id);

    try {
      const storedOrder = JSON.parse(localStorage.getItem(`team-budget-order-${draft._id}`) || '[]');
      const savedOrderIds = Array.isArray(storedOrder) ? storedOrder.filter((teamId) => defaultOrderIds.includes(teamId)) : [];
      setBudgetTeamOrderIds([...savedOrderIds, ...defaultOrderIds.filter((teamId) => !savedOrderIds.includes(teamId))]);
    } catch (error) {
      console.error('Unable to load Team Budget order', error);
      setBudgetTeamOrderIds(defaultOrderIds);
    }
  }, [draft]);

  useEffect(() => {
    if (draftId && isConnected) {
      joinDraft(draftId);
    }

    return () => {
      if (draftId && isConnected) {
        leaveDraft(draftId);
      }
    };
  }, [draftId, isConnected, joinDraft, leaveDraft]);

  useEffect(() => {
    searchPlayers();
  }, [searchPlayers]);

  const handlePlayerClick = (player: Player) => {
    if (player.isDrafted) return;

    if (!draft || draft.teams.length === 0) {
      toast.error('No teams are assigned to this draft yet.');
      return;
    }

    const defaultTeamId = getCurrentTurnTeam()?._id || getUserTeam()?._id || draft.teams[0]._id;
    const seedCost = Math.max(1, Number(player.cost || 1));
    setSelectedPlayer(player);
    setAssignTeamId(defaultTeamId || '');
    setAssignCost(String(seedCost));
  };

  const selectedAssignmentTeam = draft?.teams.find((team) => team._id === assignTeamId);
  const assignmentMaxCost = selectedAssignmentTeam?.budget?.remaining || 0;

  const removePlayerFromStrategyLists = useCallback((playerMatcher: (player: Player) => boolean) => {
    setQueuePlayers((previous) => previous.filter((entry) => !playerMatcher(entry)));
  }, []);

  const removeDraftedFromStrategyLists = useCallback((nextDraft: Draft | null) => {
    if (!nextDraft) {
      return;
    }

    const draftedIds = new Set<string>();
    const draftedNames = new Set<string>();

    (nextDraft.picks || []).forEach((pick) => {
      if (!pick.isUndone) {
        draftedIds.add(String(pick.playerId || ''));
        draftedNames.add(normalizeName(pick.playerName));
      }
    });

    (nextDraft.teams || []).forEach((team) => {
      (team.players || []).forEach((player) => {
        draftedIds.add(String(player.playerId || ''));
        draftedNames.add(normalizeName(player.playerName));
      });
    });

    if (draftedIds.size === 0 && draftedNames.size === 0) {
      return;
    }

    removePlayerFromStrategyLists((player) => {
      const playerId = String(player._id || '');
      const sportRadarId = String(player.sportRadarId || '');
      const playerName = normalizeName(player.name);

      return (
        draftedIds.has(playerId) ||
        draftedIds.has(sportRadarId) ||
        draftedNames.has(playerName)
      );
    });
  }, [removePlayerFromStrategyLists]);

  // Use the player updates hook to refresh available players and keep strategy lists clean.
  usePlayerUpdates({
    refreshPlayers: refreshPlayers,
    onPlayerUpdate: (data) => {
      if (data.action === 'drafted') {
        const draftedPlayerId = String(data.playerId || '');
        removePlayerFromStrategyLists((player) => (
          String(player._id || '') === draftedPlayerId ||
          String(player.sportRadarId || '') === draftedPlayerId
        ));

        toast.success('Available players updated', {
          duration: 2000,
          icon: '🔄',
        });

        refreshDraft();
      }
    },
    enabled: true
  });

  useEffect(() => {
    removeDraftedFromStrategyLists(draft);
  }, [draft, removeDraftedFromStrategyLists]);

  useEffect(() => {
    if (!draftId) {
      return;
    }

    try {
      const raw = localStorage.getItem(`draft-strategy-${draftId}`);
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw);
      setQueuePlayers(Array.isArray(parsed.queuePlayers) ? parsed.queuePlayers : []);
    } catch (error) {
      console.error('Unable to load draft strategy state', error);
    }
  }, [draftId]);

  useEffect(() => {
    if (!draftId) {
      return;
    }

    localStorage.setItem(
      `draft-strategy-${draftId}`,
      JSON.stringify({ queuePlayers })
    );
  }, [queuePlayers, draftId]);

  const isSamePlayer = useCallback((left: Player, right: Player) => left._id === right._id, []);

  const toggleQueuePlayer = (player: Player) => {
    setQueuePlayers((previous) => {
      const exists = previous.some((entry) => isSamePlayer(entry, player));
      if (exists) {
        return previous.filter((entry) => !isSamePlayer(entry, player));
      }

      return [...previous, player];
    });
  };

  const moveQueuePlayer = (playerId: string, direction: 'up' | 'down') => {
    setQueuePlayers((previous) => {
      const index = previous.findIndex((entry) => entry._id === playerId);
      if (index < 0) {
        return previous;
      }

      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= previous.length) {
        return previous;
      }

      const next = [...previous];
      const [moved] = next.splice(index, 1);
      next.splice(nextIndex, 0, moved);
      return next;
    });
  };

  const handleBudgetTeamDragStart = (event: React.DragEvent<HTMLElement>, teamId: string) => {
    event.dataTransfer.effectAllowed = 'move';
    setDraggingBudgetTeamId(teamId);
  };

  const handleBudgetTeamDragOver = (event: React.DragEvent<HTMLElement>, teamId: string) => {
    event.preventDefault();
    if (draggingBudgetTeamId && draggingBudgetTeamId !== teamId) {
      setBudgetDragOverTeamId(teamId);
    }
  };

  const handleBudgetTeamDrop = (targetTeamId: string) => {
    if (!draggingBudgetTeamId || draggingBudgetTeamId === targetTeamId) {
      setDraggingBudgetTeamId(null);
      setBudgetDragOverTeamId(null);
      return;
    }

    setBudgetTeamOrderIds((previous) => {
      const sourceIndex = previous.indexOf(draggingBudgetTeamId);
      const targetIndex = previous.indexOf(targetTeamId);
      if (sourceIndex < 0 || targetIndex < 0) {
        return previous;
      }

      const reordered = [...previous];
      const [movedTeamId] = reordered.splice(sourceIndex, 1);
      reordered.splice(targetIndex, 0, movedTeamId);
      if (draftId) {
        localStorage.setItem(`team-budget-order-${draftId}`, JSON.stringify(reordered));
      }
      return reordered;
    });

    setDraggingBudgetTeamId(null);
    setBudgetDragOverTeamId(null);
  };

  const handleBudgetTeamDragEnd = () => {
    setDraggingBudgetTeamId(null);
    setBudgetDragOverTeamId(null);
  };

  const updateAssignCost = (nextCost: number) => {
    if (nextCost < 1) {
      setAssignCost('1');
      return;
    }

    if (assignmentMaxCost > 0) {
      setAssignCost(String(Math.min(nextCost, assignmentMaxCost)));
      return;
    }

    setAssignCost(String(nextCost));
  };

  const handleAssignPlayer = async () => {
    if (!selectedPlayer || !assignTeamId) {
      toast.error('Please choose a team before assigning.');
      return;
    }

    const parsedCost = parseInt(assignCost, 10);
    if (Number.isNaN(parsedCost) || parsedCost < 1) {
      toast.error('Cost must be at least $1.');
      return;
    }

    try {
      setAssigning(true);
      const response = await apiClient.players.assignToTeam({
        playerId: selectedPlayer.sportRadarId,
        teamId: assignTeamId,
        cost: parsedCost,
        draftId
      });

      if (response.success) {
        toast.success(response.message || `Assigned ${selectedPlayer.name}`);
        setSelectedPlayer(null);
        setAssignTeamId('');
        setAssignCost('1');
        await Promise.all([loadDraft(), searchPlayers()]);
      }
    } catch (error: any) {
      const message = error?.response?.data?.message;
      toast.error(message || 'Failed to assign player');
    } finally {
      setAssigning(false);
    }
  };

  const openPlayerNews = async () => {
    if (!selectedPlayer?._id) return;

    try {
      setShowNewsModal(true);
      setNewsLoading(true);
      setNewsError(null);

      const response = await apiClient.players.getNews(selectedPlayer._id, { limit: 10 });
      if (response.success) {
        setPlayerNews(response.data?.news || []);
      } else {
        setPlayerNews([]);
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Unable to load player news right now.';
      setNewsError(message);
      setPlayerNews([]);
    } finally {
      setNewsLoading(false);
    }
  };

  const getUserTeam = (): Team | undefined => {
    return draft?.teams.find(team => team.ownerId?._id === user?._id);
  };

  const getCurrentTurnTeam = (): Team | undefined => {
    return draft?.teams.find(team => team._id === draft.currentTeamTurn?._id);
  };

  const getDisplayedRank = (player: Player): number | null => {
    if (selectedPosition !== 'ALL') {
      return player.positionRank ?? null;
    }

    return player.overallRank ?? null;
  };

  const pushActivity = useCallback((kind: ActivityEntry['kind'], message: string, timestamp?: string) => {
    setActivityFeed((previous) => {
      const next: ActivityEntry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        kind,
        message,
        timestamp: timestamp || new Date().toISOString()
      };
      return [next, ...previous].slice(0, 80);
    });
  }, []);

  useEffect(() => {
    if (!draft?._id) {
      return;
    }

    const pickActivities = [...(draft.picks || [])]
      .filter((pick) => !pick.isUndone)
      .map((pick) => ({
        id: `pick-${draft._id}-${pick.pickNumber}`,
        kind: 'win' as const,
        message: `${pick.teamId?.name || 'A team'} drafted ${pick.playerName} for $${pick.cost}`,
        timestamp: pick.timestamp
      }));

    setActivityFeed((previous) => {
      const nonPickActivities = previous.filter((entry) => !entry.id.startsWith(`pick-${draft._id}-`));
      return [...pickActivities, ...nonPickActivities]
        .sort((left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime())
        .slice(0, 80);
    });
  }, [draft]);

  useEffect(() => {
    if (!socket || !isConnected) {
      return;
    }

    const handleAuctionStarted = (data: any) => {
      const auction = data?.auction;
      if (!auction) return;
      const teamName = auction.nominatedBy?.name || 'Unknown team';
      pushActivity('nomination', `${teamName} nominated ${auction.playerName} at $${auction.currentBid || auction.startingBid || 1}`);
    };

    const handleBidPlaced = (data: any) => {
      const teamName = data?.bid?.team || data?.auction?.currentBidder?.name || 'Unknown team';
      const amount = data?.bid?.amount ?? data?.auction?.currentBid;
      if (amount === undefined) return;
      pushActivity('bid', `${teamName} bid $${amount}`);
    };

    const handleAuctionCompleted = (data: any) => {
      const auction = data?.auction;
      if (!auction) return;
      if (!data?.winningTeam) {
        pushActivity('system', `${auction.playerName} closed with no winning bids`);
      }
      refreshDraft();
    };

    const handleDraftStarted = () => {
      pushActivity('admin', 'Draft started');
    };

    const handleDraftUpdated = () => {
      pushActivity('admin', 'Draft settings or assignments were updated');
    };

    const handlePickUndone = (data: any) => {
      pushActivity('admin', `Pick #${data?.pickNumber ?? '?'} was undone`);
    };

    const handlePickMade = (data: any) => {
      if (data?.draft) {
        setDraft(data.draft);
      } else {
        refreshDraft();
      }
    };

    const handlePlayerAssigned = (data: any) => {
      refreshDraft();
    };

    socket.on('auctionStarted', handleAuctionStarted);
    socket.on('bidPlaced', handleBidPlaced);
    socket.on('auctionCompleted', handleAuctionCompleted);
    socket.on('draftStarted', handleDraftStarted);
    socket.on('draftUpdated', handleDraftUpdated);
    socket.on('pickUndone', handlePickUndone);
    socket.on('pickMade', handlePickMade);
    socket.on('playerAssigned', handlePlayerAssigned);

    return () => {
      socket.off('auctionStarted', handleAuctionStarted);
      socket.off('bidPlaced', handleBidPlaced);
      socket.off('auctionCompleted', handleAuctionCompleted);
      socket.off('draftStarted', handleDraftStarted);
      socket.off('draftUpdated', handleDraftUpdated);
      socket.off('pickUndone', handlePickUndone);
      socket.off('pickMade', handlePickMade);
      socket.off('playerAssigned', handlePlayerAssigned);
    };
  }, [socket, isConnected, pushActivity, refreshDraft]);

  if (loading) {
    return (
      <DraftContainer>
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <LoadingSpinner size="lg" />
          </div>
        </Container>
      </DraftContainer>
    );
  }

  if (!draft) {
    return (
      <DraftContainer>
        <Container>
          <Card padding={spacing[8]}>
            <div style={{ textAlign: 'center' }}>
              <h2>{draftId ? 'Draft not found' : 'No draft selected'}</h2>
              <p>
                {draftId
                  ? "The draft you're looking for doesn't exist or you don't have access to it."
                  : 'Open an active draft from the Dashboard Recent Drafts list.'}
              </p>
            </div>
          </Card>
        </Container>
      </DraftContainer>
    );
  }

  const userTeam = getUserTeam();
  const currentTurnTeam = getCurrentTurnTeam();
  const isUserTurn = currentTurnTeam && userTeam && currentTurnTeam._id === userTeam._id;
  const firstRoundPicks = draft.draftOrder?.find((round) => round.round === 1)?.picks || [];
  const teamOrderIndex = new Map(
    firstRoundPicks.map((pick, index) => {
      const pickTeamId = typeof pick.teamId === 'string' ? pick.teamId : pick.teamId?._id;
      return [pickTeamId, index] as const;
    })
  );
  const defaultOrderedDraftTeams = firstRoundPicks.length > 0
    ? [...draft.teams].sort((a, b) => {
        const aIndex = teamOrderIndex.get(a._id);
        const bIndex = teamOrderIndex.get(b._id);

        if (aIndex === undefined && bIndex === undefined) {
          return a.teamNumber - b.teamNumber;
        }
        if (aIndex === undefined) {
          return 1;
        }
        if (bIndex === undefined) {
          return -1;
        }
        return aIndex - bIndex;
      })
    : [...draft.teams];
  const teamById = new Map(defaultOrderedDraftTeams.map((team) => [team._id, team]));
  const orderedBudgetTeamIds = [
    ...budgetTeamOrderIds,
    ...defaultOrderedDraftTeams.map((team) => team._id).filter((teamId) => !budgetTeamOrderIds.includes(teamId))
  ];
  const orderedDraftTeams = orderedBudgetTeamIds
    .map((teamId) => teamById.get(teamId))
    .filter((team): team is Team => Boolean(team));
  const totalDraftPicks = draft.settings.teamsCount * draft.settings.playersPerTeam;
  const picksCompleted = draft.totalPicks;
  const teamsFilled = draft.teams.filter(
    (team) => team.players.length >= draft.settings.playersPerTeam
  ).length;
  const progressPercent = totalDraftPicks > 0
    ? Math.min(100, Math.round((picksCompleted / totalDraftPicks) * 100))
    : 0;

  const queueIds = new Set(queuePlayers.map((player) => player._id));

  return (
    <DraftContainer>
      <DraftHeader>
        <HeaderContent>
          <DraftTopRow>
            <DraftTitleGroup>
              <DraftTitle>{draft.name}</DraftTitle>
              <StatusBadge status={draft.status}>
                <Trophy size={12} />
                {draft.status}
              </StatusBadge>
            </DraftTitleGroup>

            <DraftStatus>
              {currentTurnTeam ? (
                <TurnBadge isUserTurn={Boolean(isUserTurn)}>
                  {isUserTurn ? 'Your Turn' : `${currentTurnTeam.name} on the clock`}
                </TurnBadge>
              ) : null}
            </DraftStatus>
          </DraftTopRow>

          <DraftSummaryRow>
            <SummaryCard>
              <SummaryLabel>Picks Complete</SummaryLabel>
              <SummaryValue>{picksCompleted} / {totalDraftPicks}</SummaryValue>
              <SummaryHint>{progressPercent}% through the draft board</SummaryHint>
            </SummaryCard>

            <SummaryCard>
              <SummaryLabel>Teams Filled</SummaryLabel>
              <SummaryValue>{teamsFilled} / {draft.teams.length}</SummaryValue>
              <SummaryHint>Rosters at capacity</SummaryHint>
            </SummaryCard>

            <SummaryCard>
              <SummaryLabel>Draft Progress</SummaryLabel>
              <SummaryValue>{progressPercent}%</SummaryValue>
              <SummaryHint>Live completion rate</SummaryHint>
              <ProgressTrack>
                <ProgressFill percent={progressPercent} />
              </ProgressTrack>
            </SummaryCard>
          </DraftSummaryRow>
        </HeaderContent>
      </DraftHeader>

      <DraftBody>
        <MainContent>
          {/* Auction Bidding Section */}
          <AuctionBidding 
            draftId={draftId!} 
            teams={draft.teams}
            onAuctionComplete={loadDraft}
          />

          <SearchSection>
            <SearchBar>
              <SearchInputWrapper>
                <SearchInput
                  type="text"
                  placeholder="Search available players..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <ClearSearchButton
                    type="button"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear player search"
                    title="Clear search"
                  >
                    <X size={16} />
                  </ClearSearchButton>
                )}
              </SearchInputWrapper>
              <FilterButton variant="secondary">
                <Filter size={18} />
              </FilterButton>
              <SortSelect
                value={selectedSort}
                onChange={(e) => setSelectedSort(e.target.value as 'best' | 'adp' | 'projection' | 'name')}
                disabled={selectedPosition !== 'ALL'}
              >
                <option value="best">Best Available</option>
                <option value="adp">ADP</option>
                <option value="projection">Projection</option>
                <option value="name">Name</option>
              </SortSelect>
            </SearchBar>
            
            <PositionFilters>
              {positions.map(position => (
                <PositionFilter
                  key={position}
                  active={selectedPosition === position}
                  onClick={() => setSelectedPosition(position)}
                >
                  {position}
                </PositionFilter>
              ))}
            </PositionFilters>
          </SearchSection>

          <Card>
            <PanelTitle>
              <Search size={20} />
              Available Players
              {(searchLoading || refreshing) && <LoadingSpinner size="sm" />}
            </PanelTitle>
            
            <MobileSidebarToggle
              variant="secondary"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu size={20} />
              View Teams & Chat
            </MobileSidebarToggle>
            
            <PlayersGrid>
              {players.map(player => (
                (() => {
                  return (
                <PlayerCard
                  key={player._id}
                  isDrafted={player.isDrafted}
                  onClick={() => handlePlayerClick(player)}
                >
                  <PlayerHeader>
                    <PlayerIdentity>
                      {player.imageUrl ? (
                        <PlayerAvatar
                          src={player.imageUrl}
                          alt={`${player.name} headshot`}
                          loading="lazy"
                        />
                      ) : (
                        <PlayerAvatarFallback>
                          {getPlayerInitials(player.name, player.position)}
                        </PlayerAvatarFallback>
                      )}

                      <PlayerInfo>
                        <PlayerName>{player.name}</PlayerName>
                        <PlayerDetails>
                          {player.team}
                          {` • Bye ${player.byeWeek ?? '--'}`}
                          {player.depthChartRole ? ` • ${player.depthChartRole}` : ''}
                        </PlayerDetails>
                      </PlayerInfo>
                    </PlayerIdentity>

                    <PlayerBadges>
                      <PlayerQuickActions>
                        <PlayerActionButton
                          type="button"
                          title="Add to queue"
                          $active={queueIds.has(player._id)}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleQueuePlayer(player);
                          }}
                        >
                          <Plus size={13} />
                        </PlayerActionButton>
                      </PlayerQuickActions>
                      <PositionBadge position={player.position}>
                        {player.position}
                      </PositionBadge>
                      <InjuryBadge $healthy={String(player.injuryStatus || '').toLowerCase() === 'healthy'}>
                        {player.injuryStatus}
                      </InjuryBadge>
                    </PlayerBadges>
                  </PlayerHeader>

                  <PlayerStats>
                    <StatItem>
                      <StatValue>#{getDisplayedRank(player) ?? '--'}</StatValue>
                      <StatLabel>{selectedPosition === 'ALL' ? 'Rank' : 'Pos Rank'}</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{player.projectedPoints.toFixed(1)}</StatValue>
                      <StatLabel>Proj</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{player.adp ? player.adp.toFixed(0) : '--'}</StatValue>
                      <StatLabel>ADP</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{player.byeWeek ?? '--'}</StatValue>
                      <StatLabel>Bye</StatLabel>
                    </StatItem>
                  </PlayerStats>
                </PlayerCard>
                  );
                })()
              ))}
            </PlayersGrid>
          </Card>

        </MainContent>

        <Sidebar>
          <StrategyPanel>
            <PanelTitle>
              <Sparkles size={20} />
              My Queue
            </PanelTitle>

            <StrategySection>
              <StrategyLabel>Queue</StrategyLabel>
              {queuePlayers.length === 0 ? (
                <EmptyStrategy>Add players from cards using the + button.</EmptyStrategy>
              ) : (
                <StrategyList>
                  {queuePlayers.map((player, index) => (
                    <StrategyItem key={player._id}>
                      <StrategyItemTop>
                        <StrategyPlayerName>{index + 1}. {player.name}</StrategyPlayerName>
                        <StrategyActions>
                          <IconActionButton onClick={() => moveQueuePlayer(player._id, 'up')} title="Move up">
                            <ArrowUp size={12} />
                          </IconActionButton>
                          <IconActionButton onClick={() => moveQueuePlayer(player._id, 'down')} title="Move down">
                            <ArrowDown size={12} />
                          </IconActionButton>
                          <IconActionButton onClick={() => setQueuePlayers((previous) => previous.filter((entry) => entry._id !== player._id))} title="Remove">
                            <X size={12} />
                          </IconActionButton>
                        </StrategyActions>
                      </StrategyItemTop>
                      <StrategyMeta>{player.position} • Proj {player.projectedPoints.toFixed(1)} • Bye {player.byeWeek ?? '--'}</StrategyMeta>
                    </StrategyItem>
                  ))}
                </StrategyList>
              )}
            </StrategySection>

            <StrategySection>
              <StrategyLabel>Tip</StrategyLabel>
              <EmptyStrategy>Add players from cards using the + button and reorder them to build your draft plan.</EmptyStrategy>
            </StrategySection>
          </StrategyPanel>

          <TeamsPanel>
            <PanelTitle>
              <Users size={20} />
              Team Budgets
            </PanelTitle>
            
            <TeamsList>
              {orderedDraftTeams.map(team => (
                (() => {
                  const totalBudget = Math.max(team.budget.total || 0, 1);
                  const spent = Math.max(team.budget.spent || 0, 0);
                  const remaining = Math.max(team.budget.remaining || 0, 0);
                  const spentPercent = clampPercent((spent / totalBudget) * 100);
                  const remainingPercent = clampPercent((remaining / totalBudget) * 100);
                  const maxBidNow = getMaxBidNow(team, draft.settings.playersPerTeam);

                  return (
                    <TeamItem
                      key={team._id}
                      draggable
                      isCurrentTurn={currentTurnTeam?._id === team._id}
                      isUserTeam={userTeam?._id === team._id}
                      isDragging={draggingBudgetTeamId === team._id}
                      isDragOver={budgetDragOverTeamId === team._id}
                      onDragStart={(event) => handleBudgetTeamDragStart(event, team._id)}
                      onDragOver={(event) => handleBudgetTeamDragOver(event, team._id)}
                      onDrop={() => handleBudgetTeamDrop(team._id)}
                      onDragEnd={handleBudgetTeamDragEnd}
                      title="Drag to reorder Team Budgets"
                    >
                      <TeamDragHandle aria-hidden="true"><GripVertical size={18} /></TeamDragHandle>
                      <TeamInfo>
                        <TeamName>
                          {team.name}
                          {userTeam?._id === team._id && ' (You)'}
                        </TeamName>
                        <TeamStats>
                          {team.players.length}/{draft.settings.playersPerTeam} players
                        </TeamStats>
                      </TeamInfo>

                      <BudgetDisplay>
                        <BudgetAmount remainingPercent={remainingPercent}>
                          ${remaining}
                        </BudgetAmount>
                        <BudgetLabel>{Math.round(remainingPercent)}% remaining</BudgetLabel>
                        <BudgetMeta>
                          <span>Spent ${spent}</span>
                          <span>Max ${maxBidNow}</span>
                        </BudgetMeta>
                        <BudgetBarTrack>
                          <BudgetSpentSegment percent={spentPercent} />
                          <BudgetRemainingSegment percent={remainingPercent} remainingPercent={remainingPercent} />
                        </BudgetBarTrack>
                      </BudgetDisplay>
                    </TeamItem>
                  );
                })()
              ))}
            </TeamsList>
          </TeamsPanel>

          <ActivityPanel>
            <PanelTitle>
              <Activity size={20} />
              Activity Timeline
            </PanelTitle>
            <ActivityList>
              {activityFeed.length === 0 ? (
                <ActivityEmptyState>No events yet. Nominations and bids will appear here.</ActivityEmptyState>
              ) : (
                activityFeed.map((entry) => (
                  <ActivityItem key={entry.id}>
                    <ActivityDot kind={entry.kind} />
                    <ActivityContent>
                      <ActivityTopRow>
                        <ActivityTypeBadge kind={entry.kind}>{getActivityLabel(entry.kind)}</ActivityTypeBadge>
                        <ActivityTime>{formatActivityTime(entry.timestamp)}</ActivityTime>
                      </ActivityTopRow>
                      <ActivityMessage>{entry.message}</ActivityMessage>
                    </ActivityContent>
                  </ActivityItem>
                ))
              )}
            </ActivityList>
          </ActivityPanel>

          <Chat 
            draftId={draftId!} 
            activeUsers={activeUsers}
            currentUsername={user?.username || ''}
          />
        </Sidebar>

        {/* Mobile Sidebar Overlay */}
        <MobileSidebarOverlay isOpen={isSidebarOpen}>
          <MobileSidebarContent>
            <MobileSidebarHeader>
              <h3 style={{ color: colors.white, margin: 0 }}>Teams & Chat</h3>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsSidebarOpen(false)}
              >
                <X size={16} />
              </Button>
            </MobileSidebarHeader>
            
            <TeamsPanel>
              <PanelTitle>
                <Users size={20} />
                Team Budgets
              </PanelTitle>
              
              <TeamsList>
                {orderedDraftTeams.map(team => (
                  (() => {
                    const totalBudget = Math.max(team.budget.total || 0, 1);
                    const spent = Math.max(team.budget.spent || 0, 0);
                    const remaining = Math.max(team.budget.remaining || 0, 0);
                    const spentPercent = clampPercent((spent / totalBudget) * 100);
                    const remainingPercent = clampPercent((remaining / totalBudget) * 100);
                    const maxBidNow = getMaxBidNow(team, draft.settings.playersPerTeam);
                    const isExpanded = expandedMobileTeamId === team._id;

                    return (
                      <div key={team._id}>
                        <MobileTeamItem
                          as="button"
                          type="button"
                          draggable
                          isCurrentTurn={currentTurnTeam?._id === team._id}
                          isUserTeam={userTeam?._id === team._id}
                          isDragging={draggingBudgetTeamId === team._id}
                          isDragOver={budgetDragOverTeamId === team._id}
                          onDragStart={(event) => handleBudgetTeamDragStart(event, team._id)}
                          onDragOver={(event) => handleBudgetTeamDragOver(event, team._id)}
                          onDrop={() => handleBudgetTeamDrop(team._id)}
                          onDragEnd={handleBudgetTeamDragEnd}
                          onClick={() => setExpandedMobileTeamId(isExpanded ? null : team._id)}
                          aria-expanded={isExpanded}
                          title="Drag to reorder Team Budgets"
                        >
                          <TeamDragHandle aria-hidden="true"><GripVertical size={18} /></TeamDragHandle>
                          <TeamInfo>
                            <TeamName>
                              {team.name}
                              {userTeam?._id === team._id && ' (You)'}
                            </TeamName>
                            <TeamStats>
                              {team.players.length}/{draft.settings.playersPerTeam} players <ChevronDown size={14} />
                            </TeamStats>
                          </TeamInfo>

                          <BudgetDisplay>
                            <BudgetAmount remainingPercent={remainingPercent}>
                              ${remaining}
                            </BudgetAmount>
                            <BudgetLabel>{Math.round(remainingPercent)}% remaining</BudgetLabel>
                            <BudgetMeta>
                              <span>Spent ${spent}</span>
                              <span>Max ${maxBidNow}</span>
                            </BudgetMeta>
                            <BudgetBarTrack>
                              <BudgetSpentSegment percent={spentPercent} />
                              <BudgetRemainingSegment percent={remainingPercent} remainingPercent={remainingPercent} />
                            </BudgetBarTrack>
                          </BudgetDisplay>
                        </MobileTeamItem>
                        {isExpanded && (
                          <MobileTeamRoster>
                            {team.players.length === 0 ? (
                              <MobileRosterEmpty>No players drafted yet.</MobileRosterEmpty>
                            ) : (
                              team.players.map((player) => (
                                <MobileRosterPlayer key={player.playerId}>
                                  <span>{player.playerName} ({player.position} - {player.team})</span>
                                  <span>${player.cost}</span>
                                </MobileRosterPlayer>
                              ))
                            )}
                          </MobileTeamRoster>
                        )}
                      </div>
                    );
                  })()
                ))}
              </TeamsList>
            </TeamsPanel>

            <ActivityPanel>
              <PanelTitle>
                <Activity size={20} />
                Activity Timeline
              </PanelTitle>
              <ActivityList>
                {activityFeed.length === 0 ? (
                  <ActivityEmptyState>No events yet. Nominations and bids will appear here.</ActivityEmptyState>
                ) : (
                  activityFeed.map((entry) => (
                    <ActivityItem key={entry.id}>
                      <ActivityDot kind={entry.kind} />
                      <ActivityContent>
                        <ActivityTopRow>
                          <ActivityTypeBadge kind={entry.kind}>{getActivityLabel(entry.kind)}</ActivityTypeBadge>
                          <ActivityTime>{formatActivityTime(entry.timestamp)}</ActivityTime>
                        </ActivityTopRow>
                        <ActivityMessage>{entry.message}</ActivityMessage>
                      </ActivityContent>
                    </ActivityItem>
                  ))
                )}
              </ActivityList>
            </ActivityPanel>

            <Chat 
              draftId={draftId!} 
              activeUsers={activeUsers}
              currentUsername={user?.username || ''}
            />
          </MobileSidebarContent>
        </MobileSidebarOverlay>

        <MobileQuickDock>
          <Button
            variant="secondary"
            onClick={() => setIsSidebarOpen(true)}
            fullWidth
          >
            <Users size={16} />
            Teams & Chat
          </Button>
          <Button
            variant="secondary"
            onClick={refreshPlayers}
            disabled={refreshing || searchLoading}
            fullWidth
          >
            <RefreshCw size={16} />
            {refreshing || searchLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </MobileQuickDock>

        <MobileQuickDockSpacer />

        {selectedPlayer && (
          <AssignModalOverlay>
            <AssignModalContent>
              <AssignModalTitle>Assign Player</AssignModalTitle>
              <AssignModalText>
                Selected {selectedPlayer.name} ({selectedPlayer.position}) - {selectedPlayer.team}
              </AssignModalText>

              <NewsLinkButton type="button" onClick={openPlayerNews} disabled={assigning}>
                View recent player news
              </NewsLinkButton>

              <FormRow>
                <FormLabel htmlFor="assign-team">Team</FormLabel>
                <Select
                  id="assign-team"
                  value={assignTeamId}
                  onChange={(e) => setAssignTeamId(e.target.value)}
                  disabled={assigning}
                >
                  {draft.teams.map((team) => (
                    <option key={team._id} value={team._id}>
                      {team.name} (Team #{team.teamNumber}) - ${team.budget.remaining}
                    </option>
                  ))}
                </Select>
              </FormRow>

              <FormRow>
                <FormLabel htmlFor="assign-cost">Cost ($)</FormLabel>
                <Input
                  id="assign-cost"
                  type="number"
                  min={1}
                  step={1}
                  value={assignCost}
                  max={assignmentMaxCost || undefined}
                  onFocus={() => {
                    if (assignCost === '1') {
                      setAssignCost('');
                    }
                  }}
                  onChange={(e) => {
                    if (e.target.value === '') {
                      setAssignCost('');
                      return;
                    }
                    updateAssignCost(parseInt(e.target.value, 10));
                  }}
                  disabled={assigning}
                />
                <CostQuickActions>
                  {[1, 2, 5, 10].map((increment) => {
                    const currentCost = parseInt(assignCost, 10) || 1;
                    const nextCost = currentCost + increment;
                    const disabled = assignmentMaxCost > 0 ? nextCost > assignmentMaxCost : false;

                    return (
                      <CostQuickButton
                        key={increment}
                        variant="secondary"
                        size="sm"
                        onClick={() => updateAssignCost(nextCost)}
                        disabled={assigning || disabled}
                      >
                        +${increment}
                      </CostQuickButton>
                    );
                  })}
                  <CostQuickButton
                    variant="secondary"
                    size="sm"
                    onClick={() => updateAssignCost(assignmentMaxCost || 1)}
                    disabled={assigning || assignmentMaxCost < 1}
                  >
                    Max
                  </CostQuickButton>
                </CostQuickActions>
              </FormRow>

              <ModalActions>
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (!assigning) {
                      setSelectedPlayer(null);
                      setAssignTeamId('');
                      setAssignCost('1');
                    }
                  }}
                  disabled={assigning}
                >
                  Cancel
                </Button>
                <Button onClick={handleAssignPlayer} disabled={assigning || !assignTeamId}>
                  {assigning ? 'Assigning...' : 'Assign Player'}
                </Button>
              </ModalActions>
            </AssignModalContent>
          </AssignModalOverlay>
        )}

        {showNewsModal && selectedPlayer && (
          <NewsModalOverlay>
            <NewsModalContent>
              <NewsModalTitle>Recent News: {selectedPlayer.name}</NewsModalTitle>
              <NewsMeta>{selectedPlayer.team} • {selectedPlayer.position}</NewsMeta>

              {newsLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: spacing[6] }}>
                  <LoadingSpinner size="lg" />
                </div>
              )}

              {!newsLoading && newsError && (
                <p style={{ color: colors.error, margin: 0 }}>{newsError}</p>
              )}

              {!newsLoading && !newsError && playerNews.length === 0 && (
                <p style={{ color: colors.grey[400], margin: 0 }}>No recent news found for this player.</p>
              )}

              {!newsLoading && !newsError && playerNews.length > 0 && (
                <NewsList>
                  {playerNews.map((item) => (
                    <NewsItemCard key={item.id}>
                      <NewsItemTitle>{item.title}</NewsItemTitle>
                      <NewsItemBody>{item.content || 'No summary available.'}</NewsItemBody>
                      <NewsItemMeta>
                        <span>{item.source || 'SportsDataIO'}</span>
                        {item.publishedAt && <span>{item.publishedAt}</span>}
                        {item.url && (
                          <a href={item.url} target="_blank" rel="noreferrer">
                            Open Source
                          </a>
                        )}
                      </NewsItemMeta>
                    </NewsItemCard>
                  ))}
                </NewsList>
              )}

              <ModalActions>
                <Button
                  variant="secondary"
                  onClick={() => setShowNewsModal(false)}
                >
                  Close
                </Button>
              </ModalActions>
            </NewsModalContent>
          </NewsModalOverlay>
        )}
      </DraftBody>
    </DraftContainer>
  );
};

export default DraftPage;
